import React, { useState, useEffect } from 'react';

interface Contact {
  email: string;
  name: string;
  job_role: string;
  school_trust: string;
  email_guess?: string;
}

interface SchoolContactSearchDashboardProps {
  onBack: () => void;
}

const SchoolContactSearchDashboard: React.FC<
  SchoolContactSearchDashboardProps
> = ({ onBack }) => {
  const [schoolName, setSchoolName] = useState('');
  const [jobRoles, setJobRoles] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [sortConfig, setSortConfig] = useState<
    { key: keyof Contact; direction: 'asc' | 'desc' } | null
  >(null);
  const [showCopied, setShowCopied] = useState(false);

  const copyToClipboard = (text: string) => {
    if (!text) return;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setShowCopied(true);
        setTimeout(() => setShowCopied(false), 1000);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Failed to copy text: ', err);
      });
  };

  useEffect(() => {
    const savedContacts = localStorage.getItem('schoolContactSearchContacts');
    if (savedContacts) {
      try {
        setContacts(JSON.parse(savedContacts));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to parse contacts from localStorage', error);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      'schoolContactSearchContacts',
      JSON.stringify(contacts)
    );
  }, [contacts]);

  const sortedContacts = React.useMemo(() => {
    if (!sortConfig) {
      return contacts;
    }
    return [...contacts].sort((a, b) => {
      const aVal = (a[sortConfig.key] || '').toLowerCase();
      const bVal = (b[sortConfig.key] || '').toLowerCase();
      if (aVal < bVal) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [contacts, sortConfig]);

  const totalPages = Math.ceil(sortedContacts.length / itemsPerPage);
  const pagedContacts = React.useMemo(
    () =>
      sortedContacts.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      ),
    [sortedContacts, currentPage]
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages || 1);
    }
  }, [totalPages, currentPage]);

  const requestSort = (key: keyof Contact) => {
    setSortConfig((prev) => {
      if (prev && prev.key === key && prev.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const handleDelete = (contact: Contact) => {
    setContacts((prev) => prev.filter((c) => c !== contact));
  };

  const exportToExcel = () => {
    if (contacts.length === 0) {
      return;
    }

    const headers = [
      'Name',
      'Job Role',
      'School/Trust',
      'Email',
      'Email Guess',
      'Nothing Returned',
    ];

    const rows = contacts.map((c) => [
      c.name,
      c.job_role,
      c.school_trust,
      c.email && c.email.trim().toUpperCase() !== 'NONE' ? c.email : '',
      (!c.email || c.email.trim().toUpperCase() === 'NONE') && c.email_guess
        ? c.email_guess
        : '',
      (!c.email || c.email.trim().toUpperCase() === 'NONE') && !c.email_guess
        ? '✓'
        : '',
    ]);

    const headerRow =
      '<tr>' +
      headers
        .map((h) => `<th style="width:229px">${h}</th>`) // 229px width
        .join('') +
      '</tr>';

    const bodyRows = rows
      .map(
        (row) =>
          '<tr>' +
          row
            .map((field) => `<td style="width:229px">${field}</td>`) // 229px width
            .join('') +
          '</tr>'
      )
      .join('');

    const htmlContent =
      '<html><head><meta charset="UTF-8" /></head><body><table>' +
      headerRow +
      bodyRows +
      '</table></body></html>';

    const blob = new Blob([htmlContent], {
      type: 'application/vnd.ms-excel;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'school_contacts.xlsx');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleReset = () => {
    if (
      window.confirm('Are you sure you want to reset your contacts table?')
    ) {
      setContacts([]);
      setCurrentPage(1);
      localStorage.removeItem('schoolContactSearchContacts');
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setIsProcessing(true);
      const response = await fetch(
        'https://n8n.myphizz.com/webhook/fa429c39-b314-4357-bb6b-f07c8b8d5aa5',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ schoolName, jobRoles }),
        }
      );
      const body = await response.json();
      if (Array.isArray(body)) {
        const newContacts = body.map((c: Contact) => {
          const normalizedEmail = c.email?.trim().toUpperCase();
          if (c.email && normalizedEmail !== 'NONE') {
            return c;
          }

          const existing = [...contacts, ...body].find(
            (ct) => ct.school_trust === c.school_trust && ct.email
          );
          let domain = '';
          if (existing) {
            domain = existing.email.split('@')[1];
          } else {
            domain = c.school_trust.toLowerCase().replace(/\s+/g, '') + '.com';
          }

          const nameParts = c.name.trim().toLowerCase().split(' ');
          let guess = `info@${domain}`;
          if (nameParts.length >= 2) {
            guess = `${nameParts[0]}.${nameParts[nameParts.length - 1]}@${domain}`;
          } else if (nameParts.length === 1) {
            guess = `${nameParts[0]}@${domain}`;
          }

          return { ...c, email: '', email_guess: guess };
        });
        setContacts((prev) => [...prev, ...newContacts]);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error submitting form', error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="relative flex flex-col items-center mt-10 p-4 min-h-screen bg-white text-black">
      {showCopied && (
        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-3 py-1 rounded flex items-center">
          <span>Copied</span>
          <span className="ml-1">✓</span>
        </div>
      )}
      <h1 className="text-3xl font-bold mb-2 text-black">School Contact Search Dashboard</h1>
      <form className="flex flex-col items-start mb-4" onSubmit={handleSubmit}>
        <label className="mb-2">
          School/Trust Name
          <input
            type="text"
            className="border ml-2 bg-white text-black"
            value={schoolName}
            onChange={(e) => setSchoolName(e.target.value)}
          />
        </label>
        <label className="mb-2">
          Job Roles
          <input
            type="text"
            className="border ml-2 bg-white text-black"
            value={jobRoles}
            onChange={(e) => setJobRoles(e.target.value)}
          />
        </label>
        <div className="flex space-x-2">
          <button
            type="submit"
            className="text-white bg-green-500 px-4 py-1 rounded"
          >
            Submit
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="text-white bg-green-500 px-4 py-1 rounded"
          >
            Reset Table
          </button>
        </div>
      </form>
      {isProcessing && (
        <div className="text-black mb-2">Processing...</div>
      )}
      {contacts.length > 0 && (
        <>
          <table className="w-full table-auto border-collapse mb-4 bg-white text-black border-2 border-black">
            <thead>
              <tr>
                <th
                  className="border border-black px-2 py-1 cursor-pointer text-xl font-bold w-[170px]"
                  onClick={() => requestSort('name')}
                >
                  Name
                </th>
                <th
                  className="border border-black px-2 py-1 cursor-pointer text-xl font-bold w-[170px]"
                  onClick={() => requestSort('job_role')}
                >
                  Job Role
                </th>
                <th
                  className="border border-black px-2 py-1 cursor-pointer text-xl font-bold w-[170px]"
                  onClick={() => requestSort('school_trust')}
                >
                  School/Trust
                </th>
                <th
                  className="border border-black px-2 py-1 cursor-pointer text-xl font-bold w-[170px]"
                  onClick={() => requestSort('email')}
                >
                  Email
                </th>
                <th
                  className="border border-black px-2 py-1 cursor-pointer text-xl font-bold w-[170px]"
                  onClick={() => requestSort('email_guess')}
                >
                  Email Guess
                </th>
                <th className="border border-black px-2 py-1 text-xl font-bold w-[170px]">Remove</th>
              </tr>
            </thead>
            <tbody>
              {pagedContacts.map((c) => (
                <tr key={`${c.name}-${c.job_role}-${c.school_trust}`}>
                  <td className="border border-black px-2 py-1 text-base w-[170px]">{c.name}</td>
                  <td className="border border-black px-2 py-1 text-base w-[170px]">{c.job_role}</td>
                  <td className="border border-black px-2 py-1 text-base w-[170px]">{c.school_trust}</td>
                  <td className="border border-black px-2 py-1 text-base w-[170px]">
                    {c.email && c.email.trim().toUpperCase() !== 'NONE' && (
                      <div className="flex items-center">
                        <span>{c.email}</span>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(c.email)}
                          className="ml-2"
                        >
                          📎
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="border border-black px-2 py-1 text-base w-[170px]">
                    {(!c.email || c.email.trim().toUpperCase() === 'NONE') &&
                      c.email_guess && (
                        <div className="flex items-center">
                          <span>{c.email_guess}</span>
                          <button
                            type="button"
                            onClick={() => copyToClipboard(c.email_guess || '')}
                            className="ml-2"
                          >
                              📎
                          </button>
                        </div>
                      )}
                  </td>
                  <td className="border border-black px-2 py-1 text-center text-base w-[170px]">
                    <button
                      type="button"
                      onClick={() => handleDelete(c)}
                      className="text-black"
                    >
                      ❌
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        <div className="flex mb-2 w-full">
          {currentPage > 1 && (
            <button
              type="button"
              onClick={() => setCurrentPage(1)}
              className="text-white bg-green-500 px-2 py-1 rounded text-sm"
            >
              Back
            </button>
          )}
          {currentPage < totalPages && (
            <button
              type="button"
              onClick={() => setCurrentPage((p) => p + 1)}
              className="text-white bg-green-500 px-2 py-1 rounded text-sm ml-auto"
            >
              Next
            </button>
          )}
        </div>
        <button
          className="text-white bg-green-500 px-4 py-1 rounded mb-4"
          onClick={exportToExcel}
        >
            Export to Excel
          </button>
        </>
      )}
      <button className="text-black underline" onClick={onBack}>
        Back to Dashboard
      </button>
    </div>
  );
};

export default SchoolContactSearchDashboard;
